import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { DATE_FORMATS, formatDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import {
	PerformanceVestingHurdleRes,
	PerformanceVestingMetricType,
	VestingTermGradedRes,
	VestingTermStraightLineRes,
	VestingTermSummaryRes,
	VestingTermTimeUnitType,
	VestingTermType,
} from '@dealstack/shared/lib/generated';
import { orderBy } from 'lodash';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export const generateUniformVestingMessage = (
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>,
	vestingTerm?: VestingTermStraightLineRes,
	startDate?: Date,
	txDate?: Date
) => {
	if (!vestingTerm) {
		return '';
	}

	const { periodTimeUnit, interval, period, cliff } = vestingTerm;
	const isMonthly = periodTimeUnit === VestingTermTimeUnitType.MONTHLY;
	const isYearly = periodTimeUnit === VestingTermTimeUnitType.YEARLY;

	const getUnitString = () => {
		if (isMonthly) {
			return 'vesting_terms.straight_line.title.unit_monthly';
		}
		if (isYearly) {
			return 'vesting_terms.straight_line.title.unit_yearly';
		}
		return '';
	};

	const getIntervalString = () => {
		if (isMonthly) {
			return interval > 1
				? 'vesting_terms.straight_line.title.interval_months'
				: 'vesting_terms.straight_line.title.interval_month';
		}
		if (isYearly) {
			return interval > 1
				? 'vesting_terms.straight_line.title.interval_years'
				: 'vesting_terms.straight_line.title.interval_year';
		}
		return '';
	};

	const getPeriodString = () => {
		if (isMonthly) {
			return period > 1
				? 'vesting_terms.straight_line.title.period_months'
				: 'vesting_terms.straight_line.title.period_month';
		}
		if (isYearly) {
			return period > 1
				? 'vesting_terms.straight_line.title.period_years'
				: 'vesting_terms.straight_line.title.period_year';
		}
		return '';
	};

	const getCliffString = () => {
		if (isMonthly) {
			return cliff > 1
				? 'vesting_terms.straight_line.title.cliff_months'
				: 'vesting_terms.straight_line.title.cliff_month';
		}
		if (isYearly) {
			return cliff > 1
				? 'vesting_terms.straight_line.title.cliff_years'
				: 'vesting_terms.straight_line.title.cliff_year';
		}
		return '';
	};

	const straightLineUnitString = tCommonUi(getUnitString());
	const straightLineIntervalString = tCommonUi(getIntervalString(), { interval });
	const straightLinePeriodString = tCommonUi(getPeriodString(), { period });
	const straightLineUniformString = tCommonUi('vesting_terms.straight_line.title.uniform');
	const straightLineCliffString = cliff ? tCommonUi(getCliffString(), { cliff }) : '.';
	const startDateString = generateStartDateMessage(tCommonUi, startDate, txDate);

	return (
		straightLineUnitString +
		straightLineIntervalString +
		straightLinePeriodString +
		straightLineUniformString +
		straightLineCliffString +
		startDateString
	);
};

export const generateNonUniformVestingMessage = (
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>,
	vestingTerm?: VestingTermGradedRes,
	startDate?: Date,
	txDate?: Date
) => {
	if (!vestingTerm) {
		return '';
	}

	const { duration, percentage } = vestingTerm;

	const titleTranslationKey =
		duration > 1
			? 'vesting_terms.graded_term.title.months'
			: 'vesting_terms.graded_term.title.one_month';
	const vestingTermMessage = tCommonUi(titleTranslationKey, {
		gradedSecuritiesPercentage: percentage,
		gradedMonthTotal: duration,
	});
	const startDateString = generateStartDateMessage(tCommonUi, startDate, txDate);

	return `${vestingTermMessage}${startDateString}`;
};

export const generateVestingSummaryMessage = (
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>,
	vestingTerm?: VestingTermSummaryRes,
	startDate?: Date,
	txDate?: Date
) => {
	let message = '';
	if (!vestingTerm) {
		return message;
	}

	if (vestingTerm.type === VestingTermType.GRADED) {
		const orderedTerms = orderBy(vestingTerm.vestingTermGraded, 'duration');
		const gradedTermMessages = orderedTerms.map((gradedTerm) =>
			generateNonUniformVestingMessage(tCommonUi, gradedTerm, startDate)
		);
		message = gradedTermMessages.join(', ');
	}

	if (vestingTerm.type === VestingTermType.STRAIGHT_LINE) {
		message = generateUniformVestingMessage(
			tCommonUi,
			vestingTerm.vestingTermStraightLine,
			startDate,
			txDate
		);
	}

	return message;
};

export const generatePerformanceRatchetTierDescription = (
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>,
	tier: PerformanceVestingHurdleRes,
	metricType: PerformanceVestingMetricType
): string => {
	switch (metricType) {
		case PerformanceVestingMetricType.MOIC:
			return tCommonUi('vesting_terms.performance.tiers.description_moic', {
				percentage: tier.incrementalPercentage,
				threshold: tier.moicThreshold,
			});
		case PerformanceVestingMetricType.XIRR:
			return tCommonUi('vesting_terms.performance.tiers.description_irr', {
				percentage: tier.incrementalPercentage,
				threshold: tier.xirrThreshold,
			});
		case PerformanceVestingMetricType.MOIC_AND_XIRR:
			return tCommonUi('vesting_terms.performance.tiers.description_moic_and_irr', {
				percentage: tier.incrementalPercentage,
				moicThreshold: tier.moicThreshold,
				irrThreshold: tier.xirrThreshold,
			});
		default:
			return 'Unknown Metric';
	}
};

export const getPerformanceMetricTypeLabel = (
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>,
	type: PerformanceVestingMetricType
) => {
	switch (type) {
		case PerformanceVestingMetricType.MOIC:
			return tCommonUi('vesting_terms.performance.metric.moic');
		case PerformanceVestingMetricType.XIRR:
			return tCommonUi('vesting_terms.performance.metric.irr');
		case PerformanceVestingMetricType.MOIC_AND_XIRR:
			return tCommonUi('vesting_terms.performance.metric.moic_and_irr');
		default:
			return tCommonUi('vesting_terms.performance.metric.unknown');
	}
};

export const generatePerformanceRatchetTierSummary = (
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>,
	tier: PerformanceVestingHurdleRes,
	metricType: PerformanceVestingMetricType
): string | undefined => {
	if (metricType === PerformanceVestingMetricType.MOIC_AND_XIRR) {
		return;
	}

	if (tier.cumulativePercentage === undefined) {
		return;
	}

	return tCommonUi('vesting_terms.performance.tiers.cumulative_summary', {
		percentage: tier.cumulativePercentage,
	});
};

const generateStartDateMessage = (
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>,
	startDate?: Date,
	txDate?: Date
) => {
	if (startDate && txDate && startDate < txDate) {
		return tCommonUi('vesting_terms.start_date', {
			startDate: formatDateToUI(startDate, DATE_FORMATS.MONTH_DAY_YEAR),
		});
	}

	return '';
};
